import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import Badge from "react-bootstrap/Badge";
import { faArrowUpRightFromSquare } from "@fortawesome/free-solid-svg-icons/faArrowUpRightFromSquare";
import { resources } from "@constants/hardcoded-strings";
import { DISPLAY_DATE_WITH_TIME } from "@constants/date-formats";
import { REPORTS_TRANSACTIONS_DETAILS_URL } from "@constants/routes";
import { ButtonSizeTypes } from "@enums/button-size-types";
import { PolicyDecisionTypes } from "@enums/policy-decision-types";
import { TableSettingsTableTypes } from "@enums/table-settings-table-types";
import { useTableFilters } from "@hooks/use-table-filters";
import { FormattedVerificationTransaction } from "@idw-types/data/reports";
import { formatDateToString, sortDates } from "@utils/date-util";
import { openToNewTabUtil } from "@utils/navigation-util";
import { injectTransactionId } from "@utils/parameter-injector";
import { sortStrings } from "@utils/string-util";
import { IdwTable } from "@containers/common/idw-table/idw-table";
import { PaginationContainer } from "@containers/common/pagination-container/pagination-container";
import { IdwTableContainer } from "@containers/common/idw-table-container/idw-table-container";
import { TableCellWithButton } from "@containers/common/table-cell-with-button/table-cell-with-button";
import { createColumnHelper, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, TableState, useReactTable } from "@tanstack/react-table";
import { useDispatch } from "react-redux";
import { handleSetColumnFiltersChange, handleSetPaginationChange, handleSetSortingChange } from "@utils/react-table/react-table-util";

type VerificationTransactionsTableContainerProps = {
    verificationTransactions: Array<FormattedVerificationTransaction>;
    tableState: TableState;
};

type VerificationTransactionsTableDataRow = FormattedVerificationTransaction & {
    relyingPartyName: string;
};

export const VerificationTransactionsTableContainer = (props: VerificationTransactionsTableContainerProps) => {
    const routerLocation = useLocation();

    const { verificationTransactions, tableState } = props;
    const dispatch = useDispatch();

    const verificationTransactionTableData: Array<VerificationTransactionsTableDataRow> = useMemo(() => {
        return verificationTransactions.map(transaction => ({
            ...transaction,
            relyingPartyName: transaction.relyingParty?.name || "",
            credential: transaction.credential || "",
            score: transaction.score || "",
        }));
    }, [verificationTransactions]);

    const handleViewOnClick = (verificationTransaction: FormattedVerificationTransaction) => {
        const url = injectTransactionId(REPORTS_TRANSACTIONS_DETAILS_URL, verificationTransaction.transactionID);
        openToNewTabUtil(url, routerLocation.pathname);
    };

    const columnHelper = createColumnHelper<VerificationTransactionsTableDataRow>();
    const columns = useMemo(() => ([
        columnHelper.display({
            id: "view-verification-transaction-details",
            header: "",
            enableSorting: false,
            cell: (props) => (
                <TableCellWithButton
                    id="view-verification-transaction-details-button"
                    size={ButtonSizeTypes.SMALL}
                    variant={"outline-primary"}
                    icon={faArrowUpRightFromSquare}
                    text={null}
                    originalObject={props.row.original}
                    onClick={handleViewOnClick}
                />
            )
        }),
        columnHelper.accessor("transactionID", {
            header: resources.tables.verificationTransactions.columns.transactionId.header,
            filterFn: "fuzzyText"
        }),
        columnHelper.accessor("date", {
            header: resources.tables.verificationTransactions.columns.date.header,
            filterFn: "dateTimeFilter",
            sortingFn: (a, b) => sortDates(a.original.date, b.original.date),
            cell: (props) => props.getValue() ? <span>{formatDateToString(props.getValue(), DISPLAY_DATE_WITH_TIME)}</span> : null
        }),
        columnHelper.accessor("relyingPartyName", {
            header: resources.tables.verificationTransactions.columns.relyingParty.header,
            filterFn: "fuzzyText",
            sortingFn: (a, b) => sortStrings(a.original.relyingPartyName, b.original.relyingPartyName)
        }),
        columnHelper.accessor("serviceName", {
            header: resources.tables.verificationTransactions.columns.verificationService.header,
            filterFn: "fuzzyText",
            sortingFn: (a, b) => sortStrings(a.original.serviceName, b.original.serviceName)
        }),
        columnHelper.accessor("credential", {
            header: resources.tables.verificationTransactions.columns.credential.header,
            filterFn: "fuzzyText",
            sortingFn: (a, b) => sortStrings(a.original.credential, b.original.credential)
        }),
        columnHelper.accessor("score", {
            header: resources.tables.verificationTransactions.columns.score.header,
            filterFn: "fuzzyText"
        }),
        columnHelper.accessor("policyDecision", {
            header: resources.tables.verificationTransactions.columns.policyDecision.header,
            filterFn: "fuzzyText",
            cell: (props) => {
                const value = props.getValue();
                switch (value) {
                    case PolicyDecisionTypes.APPROVED:
                        return <Badge bg={"success"}>{value}</Badge>;
                    case PolicyDecisionTypes.DENIED:
                        return <Badge bg={"danger"}>{value}</Badge>;
                    case PolicyDecisionTypes.NO_DECISION:
                        return <Badge bg={"secondary"}>{value}</Badge>;
                    case PolicyDecisionTypes.OBLIGATION:
                        return <Badge bg={"warning"}>{value}</Badge>;
                    default:
                        return <span>{resources.common.notFound}</span>;
                }
            }
        })
    ]), [verificationTransactionTableData]);

    const { filterTypes } = useTableFilters();

    const useTableResult = useReactTable({
        columns,
        data: verificationTransactionTableData,
        state: tableState,
        filterFns: filterTypes,
        sortDescFirst: false,
        autoResetPageIndex: true,
        onSortingChange: (sortingOrUpdateFn) => handleSetSortingChange(sortingOrUpdateFn, TableSettingsTableTypes.VERIFICATION_TRANSACTIONS, tableState.sorting, dispatch),
        onColumnFiltersChange: (filtersOrUpdateFn) => handleSetColumnFiltersChange(filtersOrUpdateFn, TableSettingsTableTypes.VERIFICATION_TRANSACTIONS, tableState.columnFilters, dispatch),
        onPaginationChange: (paginationOrUpdateFn) => handleSetPaginationChange(paginationOrUpdateFn, TableSettingsTableTypes.VERIFICATION_TRANSACTIONS, tableState.pagination, dispatch),
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel()
    });

    return (
        <IdwTableContainer>
            <IdwTable
                id="verification-transactions"
                useTableResult={useTableResult}
                headerColumnKeyPrefix={"verification-transactions"}
            />
            <PaginationContainer
                idPrefix="verification-transactions"
                useTableResult={useTableResult}
            />
        </IdwTableContainer>
    );
};